import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { initiateFontUpload, updateFontUploadProgress, uploadFont, deleteFont, invalidateFontUploadError } from 'actions/fonts';

import cx from 'classnames';
import shortid from 'shortid';
import _ from 'lodash';

import { faTrash, faTrashAlt, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Modal, ProgressBar } from 'react-bootstrap';
import { FileUploadButton } from 'components/Buttons';
import { ConfirmationDialog } from 'components/Confirmation';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { toast } from "react-toastify";
import { FailureToast } from 'components/Toast';

import styles from './font.module.scss';

export default function FontDialog({ show, onClose}) {
  const dispatch = useDispatch();

  const [uploadInstances, setUploadInstances] = useState([]);
  const [selectedFont, setSelectedFont] = useState(null);

  const uploads = useSelector(state => _.get(state, 'fonts.uploads'));
  const uploadingFail = useSelector(state => _.get(state, 'fonts.uploadError', false));
  const availableFonts = useSelector(state => {
    var fonts = _.get(state, 'fonts.items') || [];

    fonts = fonts.filter(font => !font.systemDefined);
    fonts = _.orderBy(fonts, 'createdDate', 'desc');

    return fonts;
  });

  const handleUploadStart = (instances) => {
    const instance = instances[0];
    
    const upload = {
      id: instance.files[0].id,
      name: instance.files[0].name,
      type: instance.files[0].type,
      progress: 0
    }

    dispatch(initiateFontUpload(upload));
    const ttfExtension = instance.files[0].name.endsWith('.ttf');
    if (instance.files[0].type !== "font/ttf" && !ttfExtension){
      return;
    }

    setUploadInstances((state) => {
      return [
        ...state,
        {
          ...uploadInstances,
          instance: instance
        }
      ]
    });
  }

  const handleUploadProgress = (file, progress) => {
    dispatch(updateFontUploadProgress(file.id, progress));
  }

  const handleUploadComplete = (info, file) => {
    const ttfExtension = file.name.endsWith('.ttf');
    if ((info && file.type === 'font/ttf') || (info && ttfExtension)){
      const request = {
        fileName: file.name,
        signedUrl: info.signedUrl
      }

      dispatch(uploadFont(file.id, request));

      setUploadInstances((state) => {
        return state.filter(font => {
          return font.id === file.id
        })
      });
    }
  }

  const HandleMediaUploadError = ({message, onClose}) => {
    onClose();
    return toast(
      <FailureToast
        heading={`Failure`}
        description={message}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );
  }

  const handleFontDelete = (font) => {
    setSelectedFont(font);
  }

  const handleUploadErrorClose = () => {
    dispatch(invalidateFontUploadError());
  }
  
  return (
    <>
      <Modal show={show} className={styles.fontDialog} backdropClassName={styles.overlay} centered>
        <Modal.Header>Manage Your Fonts</Modal.Header>
        <Modal.Body>
          <div className={styles.uploadContainer}>
            <span>Upload fonts in TTF</span>
            <FileUploadButton name="fontFile"
              accept={'.ttf'}
              onStart={handleUploadStart}
              onProgress={handleUploadProgress}
              onComplete={handleUploadComplete}
            >
              <Button className={styles.upload} size="sm">
                <FontAwesomeIcon icon={faUpload} /> Upload Fonts
              </Button>
            </FileUploadButton>
          </div>
          <div>
          {uploadingFail && 
               <HandleMediaUploadError message={'Unsupported file format.'} onClose={handleUploadErrorClose}/>
            }
          </div>

          <div className={styles.fonts}>
            {uploads.map((upload) => {
              return <FontUploadIndicator key={upload.id} font={upload} />
            })}

            {availableFonts.map((font) => {
              return <Font key={font.id} font={font} onDelete={() => {
                handleFontDelete(font);
              }} />
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={onClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationDialog 
        show={selectedFont != null} 
        title={"Delete Font"}
        description={'This font file will be deleted from your account. This step cannot be undone.'}
        labels={{
          confirm: 'Delete',
          cancel: 'Cancel'
        }}
        onConfirm={() => {
          dispatch(deleteFont(selectedFont.id));
          setSelectedFont(null);
        }}
        onCancel={() => {
          setSelectedFont(null);
        }}
      />
    </>
  );
}

const Font = ({ font, onDelete }) => {
  Fonts.useFont([font]);
  
  return (
    <div className={cx(styles.font, {[styles.deleting]: font.deleting})}>
      <div className={styles.name}
        style={{
          fontFamily: font.fontFamily
        }}
      >
        {font.name}
      </div>
      <div className={cx(styles.actions)}>
        <button className={styles.action} onClick={onDelete} disabled={font.deleting}>
          {!font.deleting && <FontAwesomeIcon icon={faTrashAlt} />}
          {font.deleting && 'Deleting...'}
        </button>
      </div>
    </div>
  );
}

const FontUploadIndicator = ({ font }) => {
  return (
    <div className={cx(styles.font, styles.uploading)}>
      <div className={styles.details}>
        <div className={styles.name}>
          {!font.processing && `Uploading ${font.name}...`}
          {font.processing && font.name}
        </div>
        {!font.processing &&
          <ProgressBar now={font.progress} />
        }

        {font.processing &&
          <div className={styles.status}>Processing...</div>
        }
      </div>
    </div>
  );
}